import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AlumniSection = () => {
  const alumniSpotlights = [
    {
      id: 1,
      name: "Dr. Samantha Wijesinghe",
      currentPosition: "Chief Medical Officer",
      company: "Asiri Hospital Group",
      leoRole: "President (2018-2019)",
      graduationYear: 2019,
      image: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400&h=400&fit=crop&crop=face",
      careerProgression: [
        { year: 2019, role: "Junior Doctor", company: "National Hospital" },
        { year: 2021, role: "Specialist Registrar", company: "Colombo General Hospital" },
        { year: 2023, role: "Consultant Physician", company: "Asiri Hospital" },
        { year: 2024, role: "Chief Medical Officer", company: "Asiri Hospital Group" }
      ],
      currentImpact: "Leading healthcare innovation initiatives across 12 hospitals",
      leoImpact: "Established the first Leo medical outreach program, serving 5000+ patients annually",
      quote: "Leo Circle taught me that leadership in healthcare means serving beyond the hospital walls.",
      achievements: [
        "Published 15+ medical research papers",
        "Recipient of National Medical Excellence Award 2024",
        "Established free healthcare clinics in 3 rural areas"
      ],
      continuedInvolvement: "Advisory Board Member & Annual Fundraising Gala Sponsor"
    },
    {
      id: 2,
      name: "Rohan Jayawardena",
      currentPosition: "Senior Vice President",
      company: "Standard Chartered Bank",
      leoRole: "Treasurer (2017-2018)",
      graduationYear: 2018,
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face",
      careerProgression: [
        { year: 2018, role: "Management Trainee", company: "Commercial Bank" },
        { year: 2020, role: "Assistant Manager", company: "Commercial Bank" },
        { year: 2022, role: "Vice President", company: "Standard Chartered" },
        { year: 2024, role: "Senior Vice President", company: "Standard Chartered Bank" }
      ],
      currentImpact: "Managing LKR 50B+ portfolio and leading digital banking transformation",
      leoImpact: "Implemented financial literacy programs reaching 2000+ youth",
      quote: "The financial discipline I learned as Leo Treasurer became the foundation of my banking career.",
      achievements: [
        "Led digital transformation saving $2M annually",
        "Youngest SVP in company history",
        "Established microfinance program for SMEs"
      ],
      continuedInvolvement: "Corporate Partnership Coordinator & Scholarship Fund Contributor"
    },
    {
      id: 3,
      name: "Niluka Rathnayake",
      currentPosition: "Founder & CEO",
      company: "GreenTech Solutions",
      leoRole: "Environmental Chair (2019-2020)",
      graduationYear: 2020,
      image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=400&fit=crop&crop=face",
      careerProgression: [
        { year: 2020, role: "Environmental Consultant", company: "ERM Sri Lanka" },
        { year: 2021, role: "Project Manager", company: "ERM Sri Lanka" },
        { year: 2022, role: "Co-founder", company: "GreenTech Solutions" },
        { year: 2024, role: "Founder & CEO", company: "GreenTech Solutions" }
      ],
      currentImpact: "Leading sustainable technology solutions for 100+ companies",
      leoImpact: "Launched tree planting initiative that planted 10,000+ trees",
      quote: "Leo Circle showed me how environmental passion can become a successful business model.",
      achievements: [
        "Company valued at $5M within 2 years",
        "Winner of Young Entrepreneur Award 2024",
        "Reduced carbon footprint for 50+ businesses"
      ],
      continuedInvolvement: "Environmental Projects Advisor & Green Initiative Sponsor"
    }
  ];

  const alumniStats = [
    { label: "Total Alumni", value: "150+", icon: "Users" },
    { label: "Leadership Positions", value: "85%", icon: "TrendingUp" },
    { label: "Average Career Growth", value: "3.2x", icon: "BarChart3" },
    { label: "Continued Involvement", value: "70%", icon: "Heart" }
  ];

  return (
    <section className="py-16 bg-surface">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Alumni Success Stories
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Our graduates continue to make remarkable impacts in their careers and communities. 
            See how Leo Circle shaped their journey to leadership excellence.
          </p>
        </div>

        {/* Alumni Statistics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {alumniStats.map((stat, index) => (
            <div key={index} className="bg-card rounded-xl p-6 text-center card-shadow">
              <div className="w-12 h-12 hero-gradient rounded-lg flex items-center justify-center mx-auto mb-4">
                <Icon name={stat.icon} size={24} className="text-white" />
              </div>
              <div className="text-2xl lg:text-3xl font-bold text-foreground mb-2">{stat.value}</div>
              <div className="text-sm text-muted-foreground">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Featured Alumni */}
        <div className="space-y-12">
          {alumniSpotlights.map((alumni, index) => (
            <div key={alumni.id} className={`flex flex-col lg:flex-row gap-8 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              <div className="lg:w-1/3">
                <div className="bg-card rounded-xl overflow-hidden card-shadow">
                  <div className="aspect-square overflow-hidden">
                    <Image
                      src={alumni.image}
                      alt={alumni.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-foreground mb-2">{alumni.name}</h3>
                    <p className="text-primary font-medium mb-1">{alumni.currentPosition}</p>
                    <p className="text-muted-foreground text-sm mb-4">{alumni.company}</p>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm">
                        <Icon name="Award" size={16} className="text-accent mr-2" />
                        <span className="text-muted-foreground">Leo Role: {alumni.leoRole}</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <Icon name="Calendar" size={16} className="text-accent mr-2" />
                        <span className="text-muted-foreground">Graduated: {alumni.graduationYear}</span>
                      </div>
                    </div>

                    <blockquote className="text-sm italic text-muted-foreground border-l-2 border-primary pl-3">
                      "{alumni.quote}"
                    </blockquote>
                  </div>
                </div>
              </div>

              <div className="lg:w-2/3 space-y-6">
                {/* Career Progression */}
                <div className="bg-card rounded-xl p-6 card-shadow">
                  <h4 className="text-lg font-bold text-foreground mb-4 flex items-center">
                    <Icon name="TrendingUp" size={20} className="text-primary mr-2" />
                    Career Progression
                  </h4>
                  <div className="space-y-3">
                    {alumni.careerProgression.map((step, stepIndex) => (
                      <div key={stepIndex} className="flex items-center">
                        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mr-4">
                          <span className="text-primary font-bold text-sm">{step.year}</span>
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{step.role}</p>
                          <p className="text-sm text-muted-foreground">{step.company}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Impact & Achievements */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-card rounded-xl p-6 card-shadow">
                    <h4 className="text-lg font-bold text-foreground mb-4 flex items-center">
                      <Icon name="Target" size={20} className="text-accent mr-2" />
                      Current Impact
                    </h4>
                    <p className="text-muted-foreground mb-4">{alumni.currentImpact}</p>
                    <div className="space-y-2">
                      {alumni.achievements.map((achievement, achIndex) => (
                        <div key={achIndex} className="flex items-start">
                          <Icon name="CheckCircle" size={16} className="text-accent mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-muted-foreground">{achievement}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-card rounded-xl p-6 card-shadow">
                    <h4 className="text-lg font-bold text-foreground mb-4 flex items-center">
                      <Icon name="Heart" size={20} className="text-secondary mr-2" />
                      Leo Legacy
                    </h4>
                    <p className="text-muted-foreground mb-4">{alumni.leoImpact}</p>
                    <div className="bg-muted rounded-lg p-3">
                      <p className="text-sm font-medium text-foreground mb-1">Continued Involvement:</p>
                      <p className="text-sm text-muted-foreground">{alumni.continuedInvolvement}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <div className="bg-card rounded-xl p-8 card-shadow">
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Ready to Start Your Leadership Journey?
            </h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Join the ranks of successful professionals who built their leadership foundation 
              through service excellence at Leo Circle Colombo.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="default" className="hero-gradient">
                Apply for Membership
              </Button>
              <Button variant="outline">
                Connect with Alumni
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AlumniSection;